import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { TICKETS_PATHS } from '../../../../../../tickets/tickets';
import { Enums } from '../../../../../../../configs/Enums';
import { TicketViewService } from '../../../../../../tickets/ticket-view/ticket-view.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-tickets-ticket-new',
  templateUrl: './tickets-ticket.component.html',
  styleUrls: ['./tickets-ticket.component.scss'],
  standalone: false,
})
export class TicketsTicketComponent {
  TICKETS_PATHS = TICKETS_PATHS;
  Enums = Enums;

  @Input() intervalIndex;
  @Input() ticket;
  @Input() ticketsCurrentFilter;
  @Input() filter;
  @Input() brigadeAssign;
  @Input() ticketsMultipleAssign;
  @Input() isCompact: boolean = false;
  @Input() isExtraCompact: boolean = false;
  @Input() cardWidth: number = 240; // Ширина карточки в пикселях

  isRefreshingLineStatus: boolean = false;

  // Геттеры для определения что показывать в зависимости от размера
  get shouldShowVkHdStatuses(): boolean {
    return !this.isExtraCompact;
  }

  get shouldShowLineStatus(): boolean {
    return !this.isCompact && !this.isExtraCompact;
  }

  get shouldShowTveWifi(): boolean {
    return !this.isCompact && !this.isExtraCompact;
  }

  get shouldShowAddress(): boolean {
    return !this.isExtraCompact;
  }

  get shouldShowCtn(): boolean {
    return !this.isExtraCompact;
  }

  get shouldShowGpCheck(): boolean {
    return !this.isCompact && !this.isExtraCompact;
  }

  // Геттеры для стилей обрезки текста на основе реальной ширины карточки
  get districtMaxWidth(): string {
    return '100%';
  }

  get ticketNumberMaxWidth(): string {
    if (this.cardWidth < 80) return '40px';
    if (this.cardWidth < 120) return '60px';
    if (this.cardWidth < 180) return '70px';
    if (this.cardWidth < 220) return '80px';
    return '100px';
  }

  get serviceTextMaxWidth(): string {
    // Используем всю доступную ширину колонки, обрезка произойдёт только при реальном переполнении
    return '100%';
  }

  get addressMaxWidth(): string {
    return '100%';
  }

  get statusMaxWidth(): string {
    return '100%';
  }

  constructor(
    private router: Router,
    private ticketViewService: TicketViewService
  ) {}

  refreshLineStatus(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.ticket || !this.ticket.id || this.isRefreshingLineStatus) {
      return;
    }
    this.isRefreshingLineStatus = true;
    this.resolveTicketLogin().subscribe(
      (login: string) => {
        if (!login) {
          this.isRefreshingLineStatus = false;
          return;
        }
        this.ticketViewService.getHdAbonentStatus({ login }).subscribe(
          (response: any) => {
            this.isRefreshingLineStatus = false;
            if (response && response.data && response.data.status != null) {
              const code = Number(response.data.status);
              this.ticket.hd_line_status_info = [1, 2, 3, 4].indexOf(code) > -1 ? 1 : 0;
            } else {
              this.ticket.hd_line_status_info = null;
            }
          },
          () => {
            this.isRefreshingLineStatus = false;
          }
        );
      },
      () => {
        this.isRefreshingLineStatus = false;
      }
    );
  }

  private resolveTicketLogin() {
    if (this.ticket.ticket_login) {
      return of(this.ticket.ticket_login as string);
    }
    return this.ticketViewService.getTicket(this.ticket.id).pipe(
      map((res: any) => {
        const login = (res && res.data && res.data.ticket_login) || '';
        if (login) {
          this.ticket.ticket_login = login;
        }
        return login;
      })
    );
  }

  navigateToTicket(): void {
    this.router.navigate(['/' + this.TICKETS_PATHS.ticketLink + '/' + this.ticket.id]);
  }

  // Форматирует длинный адрес: для отображения в 3 строки с сокращением улицы на 3-й строке
  formatAddress(address: string): string {
    if (!address) return '';

    // Примерный лимит символов для 3 строк (зависит от ширины карточки)
    const MAX_LEN = 120;
    if (address.length <= MAX_LEN) return address;

    // Ищем часть с домом: варианты "д.", "дом", возможны дроби/корпуса
    const houseRegex = /(д\.?\s*\d+[\w\-/]*(?:\s*,?\s*(?:к\.?|корп\.?|корпус)\s*\d+[\w\-/]*)?(?:\s*,?\s*(?:стр\.?|строение)\s*\d+[\w\-/]*)?)/i;
    const match = address.match(houseRegex);

    if (match) {
      const houseInfo = match[1]; // полная информация о доме
      const beforeHouse = address.slice(0, match.index!).trim();
      const afterHouse = address.slice(match.index! + match[1].length).trim();

      // Сокращаем часть до дома, оставляя город и сокращая улицу
      const cityMatch = beforeHouse.match(/^([^,]+,?\s*)/); // первая часть до запятой (город)
      const city = cityMatch ? cityMatch[1].trim() : '';

      // Формируем сокращённый адрес: Город, ул... дом
      const shortAddress = `${city} ул... ${houseInfo}${afterHouse ? ', ' + afterHouse : ''}`;

      return shortAddress;
    }

    // Если дом не найден, просто обрезаем адрес
    return address.slice(0, MAX_LEN - 3) + '...';
  }
}
